/** The record page: one activity record, in five tabs - ADR-0212, on the shared `RecordPage`.
 *
 * **Details, Document, Calculation, Approvals, History.** A header card with the figure's key facts
 * and a status path, five tabs, the current tab's content, and a right rail (the document, what it
 * relates to, recent activity). The old tabs (Review, Original source, Correct record, Workflow,
 * Revision history) are these five: `?tab=review|source|workflow|history` still open the tab they
 * became, and *Correct this record* is a button in the header that opens its explanation.
 *
 * Three things this page settles, all from ADR-0035:
 *
 * - **A record has an address.** `/records/{id}?tab=history` is what an auditor's question sounds
 *   like, rather than "open the inventory, filter, click the row, then the fifth tab".
 * - **Work attaches to the record, not to the figure.** A flag is about the meter reading, not about
 *   its location-based half. S05's tasks, notes, flags and approvals are the Approvals tab (ADR-0037).
 * - **The revision chain is visible.** ADR-0030 derives effectiveness rather than storing it, and a
 *   superseded record has to *say* it is superseded: a restated figure that looks live is one nobody
 *   goes back to check.
 *
 * **The arithmetic is only re-rendered on the Calculation tab, and there only from the receipt**
 * (`RecordCalculation.tsx`): the receipt drawer stays rule 3's single full rendering of a figure's
 * chain, and every figure on this page is a button that opens it.
 *
 * **The reviewer's desk is deliberately not on this page.** For a *document* it exists at
 * `/evidence/{id}`, because that is where the decision is taken. An activity record that exists has
 * been through it: `AWAITING_REVIEW` is the only route into `VERIFIED`. One exception, and it is
 * narrow (ADR-0067): a figure with *no document* has no `AWAITING_REVIEW` to pass through, so the
 * Details tab offers it an acceptance (`RecordVerify.tsx`) to an Admin or Approver who is not the
 * person who stated the basis. A figure that has a document shows no control here.
 */

import { Flame, Lightning, Truck } from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { useDocument, useDocumentUrl } from "../api/evidence";
import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { useActivity, useReceipt } from "../api/queries";
import type { Activity } from "../api/queries";
import { useApprovalRoutes, useRecordWork } from "../api/work";
import type { Work } from "../api/work";
import { AssuranceAbout, HEADING as ASSURANCE_HEADING } from "../components/AssuranceAbout";
import { ReadFailed, ReadsFailed } from "../components/Loaded";
import { OutsideEngagement } from "../components/OutsideEngagement";
import { Empty, Panel } from "../components/Panel";
import { RaiseAboutThis } from "../components/RaiseAboutThis";
import type { Fact, GridColumn, RecordTabKey } from "../components/ui";
import {
  BasisChip,
  EmptyState,
  Disclosure,
  EvidenceMeter,
  FieldGrid,
  GridTable,
  InfoPopover,
  QualityMeter,
  RECORD_TABS,
  RecordPage,
  SourceChip,
  StatusChip,
  StatusPath,
  isOrigin,
} from "../components/ui";
import { exactTonnes, instantDate, statedQuantity, tonnes, unitLabel } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import type { SpineStep } from "./filingSpine";
import { PersonName } from "./PersonRef";
import { RecordCalculation } from "./RecordCalculation";
import { RecordTimeline } from "./RecordHistory";
import { RecordOther } from "./RecordOther";
import { STALE_SENTENCE } from "./RecordSignOff";
import { recordTabOf } from "./recordTabKey";
import { VerifyStatedBasis } from "./RecordVerify";
import { WorkflowTab } from "./RecordWork";
import { meterOf } from "./evidenceMeter";
import { ExtractedValues, SourceValue } from "./recordSource";
import { useSourceFields } from "./useSourceFields";
import type { SourceFields } from "./useSourceFields";
import { mayEnterData, recordPlace } from "../shell/routeTable";
import { ScreenFrame } from "./ScreenFrame";
import { SourceTextPane } from "./SourceText";
import { SCOPE_OF, STATUS_WORD, periodWords, statusOf, words } from "./scopeTable/model";
import type { FigureStatus } from "./scopeTable/model";
import { keepSearch } from "./search";
import styles from "./scopeTable/scopeTable.module.css";
import type { Screen } from "./useScreen";
import { useScreen } from "./useScreen";

const STATUS_TONE: Record<FigureStatus, "ok" | "warn" | "crit" | "neutral"> = {
  verified: "ok",
  awaiting: "warn",
  not_verified: "neutral",
  gap: "crit",
};

function Loading({ screen }: { screen: Screen }) {
  return (
    <ScreenFrame screen={screen} title="Record" crumb="loading">
      <Panel title="Record" column="1 / 13">
        <Empty>Loading…</Empty>
      </Panel>
    </ScreenFrame>
  );
}

/** One sentence for three answers, because the server gives one answer for three cases: another
 * tenant's record, a record outside an auditor's engagement and one that does not exist are the same
 * 404 on purpose - distinguishing them would tell a caller that a record exists in a tenant they have
 * no access to. */
function NotAvailable({ screen }: { screen: Screen }) {
  return (
    <ScreenFrame screen={screen} title="Record" crumb="not available">
      <Panel title="Not available" column="1 / 8">
        <OutsideEngagement noun="record" />
      </Panel>
    </ScreenFrame>
  );
}

function RecordScreenBody() {
  const screen = useScreen();
  const { recordId } = useParams({ from: "/records/$recordId" });

  // **What kind of record this is**, which is the only thing the supertype knows (EXP-15). The
  // supertype deliberately holds no title - a title denormalised there is a second copy of a name
  // that can disagree with the first - so the kind is the smallest thing that lets this page know
  // what it is looking at.
  const kindRead = useRecordWork(recordId);
  const { data: work, isPending: kindPending } = kindRead;
  const kind = work?.kind;

  // **Not asked for until the kind is known**: a request for an activity record by a risk's id is a
  // 404 the product could have predicted, and `make ui-check` fails a console error. See `useActivity`.
  const activityRead = useActivity(recordId, kind === "activity_record");
  const { data: activity, isPending, error } = activityRead;

  if (kindPending) return <Loading screen={screen} />;
  // **A failed read is not a record that does not exist** (EXP-29): `NotAvailable` says there is no
  // such record or that it falls outside an engagement, and a dropped connection produced both.
  if (kindRead.error) {
    return (
      <ScreenFrame screen={screen} title="Record" crumb="could not be read">
        <Panel title="Record" column="1 / 8">
          <ReadFailed of="This record" error={kindRead.error} onRetry={() => void kindRead.refetch()} />
        </Panel>
      </ScreenFrame>
    );
  }
  if (!kind) return <NotAvailable screen={screen} />;
  // Every other kind has its own page (EXP-15), on the same frame.
  if (kind !== "activity_record") return <RecordOther screen={screen} kind={kind} />;
  if (isPending) return <Loading screen={screen} />;
  // **A failed read is not a record that does not exist** (G-P9-12). `useActivity` answers `null` for a
  // 404 - the one answer that means *no such record here, or outside your engagement* - and throws for
  // anything else. A 500 or a dropped connection said "There is no such record in this workspace",
  // three claims at once about a record that very likely exists, and offered no way to ask again.
  if (error) {
    return (
      <ScreenFrame screen={screen} title="Record" crumb="could not be read">
        <Panel title="Record" column="1 / 8">
          <ReadFailed of="This record" error={error} onRetry={() => void activityRead.refetch()} />
        </Panel>
      </ScreenFrame>
    );
  }
  if (!activity) return <NotAvailable screen={screen} />;

  return <ActivityRecordPage screen={screen} activity={activity} work={work} />;
}

function ActivityRecordPage({
  screen,
  activity,
  work,
}: {
  screen: Screen;
  activity: Activity;
  work: Work | null | undefined;
}) {
  const navigate = useNavigate();
  const tab = recordTabOf(screen.tab);
  const goTab = (key: RecordTabKey) => screen.setTab(key);

  // **Click a value, see it on the bill** (P15 item 1). The field on show is held here, once, so the
  // links on the Details and Calculation tabs, the highlight in the text and the row in the grid are
  // one selection and cannot disagree. Choosing one opens the Document tab.
  const [focus, setFocus] = useState<string | null>(null);
  const [scrollTo, setScrollTo] = useState<{ field: string; n: number } | null>(null);
  const show = useCallback(
    (field: string | null) => {
      setFocus(field);
      if (field !== null) screen.setTab("document");
    },
    [screen],
  );
  const source = useSourceFields(
    activity.source_document_id ?? undefined,
    focus,
    show,
    activity.source_document_read,
  );

  // The headline figure: the location-based one (or the only one). Scope 2 can have two, and they
  // are never added, so the page leads with one and the Calculation tab shows both.
  const headline =
    activity.calculations.find((calc) => calc.method !== "market") ?? activity.calculations[0];
  const receiptRead = useReceipt(headline?.calculation_id);
  const scope = headline?.scope ?? SCOPE_OF[activity.category as keyof typeof SCOPE_OF] ?? "1";
  const origin = activity.origin;

  // **A receipt that has not answered is not a complete one.** The status was drawn from
  // `receiptRead.data?.is_complete ?? true`, so a figure whose receipt could not be read was called
  // *Verified*. Until the receipt answers (or when it failed) a verified figure says it is not checked
  // yet; a figure with no verifier needs no receipt to say so (G-P9-22).
  const receiptAnswered = receiptRead.data !== undefined;
  const status = statusOf({
    verification_id: activity.verification_id,
    receipt_is_complete: receiptRead.data?.is_complete ?? true,
    origin,
  });
  const statusUnchecked = Boolean(activity.verification_id) && !receiptAnswered && Boolean(headline);
  const title = `${words(activity.category)} · ${activity.facility_name} · ${periodWords(activity.period_start, activity.period_end)}`;

  const facts: Fact[] = [
    {
      label: "tCO₂e",
      testId: "record-fact-tonnes",
      value: headline ? (
        <span className={styles.actions}>
          <button
            type="button"
            className={styles.figureButton}
            data-testid="record-open-receipt"
            title={`${exactTonnes(headline.kgco2e)} tCO₂e, exact`}
            aria-label={`Receipt: ${tonnes(headline.kgco2e, 2)} tonnes CO2 equivalent`}
            onClick={() => screen.openReceipt(headline.calculation_id)}
          >
            {tonnes(headline.kgco2e, 2)}
          </button>
          <BasisChip basis={activity.basis} />
        </span>
      ) : (
        "—"
      ),
    },
    {
      label: "Quantity",
      value: (
        <SourceValue source={source} of="quantity">
          {`${statedQuantity(activity.quantity)} ${unitLabel(activity.unit)}`}
        </SourceValue>
      ),
    },
    {
      label: "Fuel or carrier",
      value: <SourceValue source={source} of="carrier">{words(activity.fuel_or_carrier) || "—"}</SourceValue>,
    },
    { label: "Data quality", value: <QualityMeter grade={isGrade(activity.quality) ? activity.quality : "D"} /> },
    { label: "Source", value: isOrigin(origin) ? <SourceChip origin={origin} /> : "—" },
    {
      label: "Status",
      testId: "record-fact-status",
      value: (
        <span className={styles.actions}>
          {statusUnchecked ? (
            <StatusChip tone="neutral" size="sm" testId="record-status-unchecked">
              Not checked
            </StatusChip>
          ) : (
            <StatusChip tone={STATUS_TONE[status]} size="sm">
              {STATUS_WORD[status]}
            </StatusChip>
          )}
          {/* Which version this is, always said: a figure that is in force says so and a replaced one
              says that instead (ADR-0030), so nobody has to infer one from the absence of the other. */}
          {activity.is_effective ? (
            <StatusChip tone="neutral" size="sm" testId="record-in-force">
              In force
            </StatusChip>
          ) : (
            <StatusChip tone="warn" size="sm" testId="record-superseded">
              Superseded
            </StatusChip>
          )}
          <InfoPopover infoKey="record.status" />
          {activity.verification_id ? null : <InfoPopover infoKey="record.no-verifier" />}
          {activity.is_effective ? null : <InfoPopover infoKey="record.superseded" />}
        </span>
      ),
    },
  ];

  const ledgered = receiptRead.data ? receiptRead.data.ledger.length > 0 : undefined;
  // **One status path, and its Approved step exists only where an approval can happen.** The step
  // used to be drawn for every figure, and with no route published for activity records it could not
  // be reached: "Approved: not yet" beside a receipt that said Traceable (U7). It is drawn when a
  // route is published for the kind or an approval already stands; otherwise the Approvals tab says
  // where a route is published (Admin, under Approval routes).
  const routes = useApprovalRoutes("activity_record");
  // A step the routes read decides is **unknown** when that read failed, not absent: dropping it made the
  // path look complete (G-P9-22).
  const approvable = Boolean(work?.standing) || (routes.data?.length ?? 0) > 0 || Boolean(routes.error);
  const steps: SpineStep[] = [
    { key: "recorded", label: origin === "manual" ? "Entered" : "Recorded", state: "done", note: "" },
    {
      key: "priced",
      label: "Priced",
      state: activity.calculations.length > 0 ? "done" : "current",
      note: "",
    },
    {
      key: "verified",
      label: "Verified",
      state: activity.verification_id ? "done" : activity.calculations.length > 0 ? "current" : "ahead",
      note: "",
    },
    ...(approvable
      ? [
          {
            // **Approved, and it drops back when the approval is stale** (P15 item 5). The server
            // compares what was approved with what is in force; a stale approval is not an approval
            // of this figure, so the step is the current one again rather than done.
            key: "approved",
            label: "Approved",
            state:
              !work || (routes.error && !work.standing)
                ? "unknown"
                : !work.standing
                  ? "ahead"
                  : work.standing.stale
                    ? "current"
                    : "done",
            note: work?.standing?.stale ? STALE_SENTENCE : "",
          } satisfies SpineStep,
        ]
      : []),
    {
      key: "ledger",
      label: "In the ledger",
      state: ledgered === undefined ? "unknown" : ledgered ? "done" : "ahead",
      note: "",
    },
  ];

  // **The evidence chain, folded under the path** (U7): the six links are the receipt's own answer to
  // "can this figure be traced", so they are one line in the same header, in the receipt's words
  // (Traceable or Incomplete), and a click opens the segments, each of which opens the part of the
  // receipt that explains it. Never a single percentage and never a count of links.
  const receipt = receiptRead.data;
  const meter = receipt ? (
    <Disclosure
      testId="evidence-disclosure"
      tone={receipt.is_complete ? "neutral" : "warn"}
      toggleLabel="the six evidence links"
      summary={
        <>
          <b>{receipt.is_complete ? "Traceable" : "Incomplete"}</b>
          {receipt.is_complete ? " · every link of the evidence chain holds" : ` · ${receipt.evidence_why_not}`}
        </>
      }
    >
      <EvidenceMeter
        links={meterOf(receipt)}
        onOpen={(link) => {
          if (headline) screen.openReceipt(headline.calculation_id, link.part);
        }}
      />
      <InfoPopover infoKey="record.evidence-meter" />
    </Disclosure>
  ) : undefined;
  // **A read that changes what the page shows says when it failed** (G-P9-22). The evidence line and
  // the Approved step each come from a read of their own; with the line gone and the step dropped the
  // page looked complete, and a figure nobody could check read as one nobody had needed to.
  const failedReads = (
    <ReadsFailed
      testId="record-read-failures"
      reads={[
        { of: "The evidence check for this figure", read: receiptRead },
        { of: "The approval routes", read: routes },
      ]}
    />
  );

  // What an approver attests, read from the receipt: the revision, the figure and the edition.
  const edition = receipt
    ? [receipt.factor.publisher, receipt.factor.edition].filter(Boolean).join(" ") || "the stated method"
    : "";
  const attest =
    headline && receipt
      ? {
          revision: Math.max(
            1,
            activity.revisions.findIndex((r) => r.activity_record_id === activity.activity_record_id) + 1,
          ),
          figure: tonnes(headline.kgco2e, 2),
          calculationId: headline.calculation_id,
          edition,
        }
      : undefined;

  const scopeTarget = scope === "2" ? "/scope-2" : scope === "3" ? "/scope-3" : "/scope-1";
  const Glyph = scope === "2" ? Lightning : scope === "3" ? Truck : Flame;

  return (
    <ScreenFrame
      screen={screen}
      title={title}
      crumb={`Scope ${scope}`}
      bodyScrolls
      lights={recordPlace("activity_record", scope)}
    >
      <RecordPage
        icon={Glyph}
        title={title}
        label={`Scope ${scope} record`}
        facts={facts}
        path={<StatusPath steps={steps} label="Where this record stands" testId="record-path" />}
        meter={
          receiptRead.error || routes.error ? (
            <>
              {failedReads}
              {meter}
            </>
          ) : (
            meter
          )
        }
        tab={tab}
        onTab={goTab}
        saying={`Showing ${RECORD_TABS.find((entry) => entry.key === tab)?.label ?? tab} for ${words(activity.category)} at ${activity.facility_name}.`}
        rail={{
          document: <RailDocument activity={activity} onOpen={() => goTab("document")} />,
          related: (
            <ul className={styles.railList}>
              <li>
                <Link
                  to="/organisation/sites/$facilityId"
                  params={{ facilityId: activity.facility_id }}
                  search={keepSearch}
                >
                  {activity.facility_name}
                </Link>
              </li>
              <li>
                <Link to={scopeTarget} search={keepSearch}>
                  Scope {scope} table
                </Link>
              </li>
            </ul>
          ),
          activity: (
            <RailRevisions
              activity={activity}
              open={(id) =>
                void navigate({
                  to: "/records/$recordId",
                  params: { recordId: id },
                  search: (prev) => ({ ...keepSearch(prev), tab: "history" }),
                })
              }
            />
          ),
        }}
      >
        {tab === "details" ? (
          <DetailsTab activity={activity} origin={origin} onReceipt={screen.openReceipt} source={source} />
        ) : null}
        {tab === "document" ? (
          <DocumentTab
            activity={activity}
            source={source}
            scrollTo={scrollTo}
            onMark={(field) => {
              setFocus(field);
              setScrollTo((prev) => ({ field, n: (prev?.n ?? 0) + 1 }));
            }}
          />
        ) : null}
        {tab === "calculation" ? (
          <RecordCalculation
            activity={activity}
            onReceipt={screen.openReceipt}
            onTab={goTab}
            source={source}
          />
        ) : null}
        {tab === "approvals" ? (
          <WorkflowTab
            recordId={activity.activity_record_id}
            kind="activity_record"
            /* **The revision a note or an approval is recorded against.** An activity record has no
               version column - ADR-0030 makes effectiveness a chain and `invalidate_superseded` is
               what ends a pending approval - so this is the record's position in its own chain,
               carried for the reader ("submitted against revision 2") rather than checked. */
            revision={Math.max(
              1,
              activity.revisions.findIndex((r) => r.activity_record_id === activity.activity_record_id) + 1,
            )}
            attest={attest}
            onReceipt={screen.openReceipt}
          />
        ) : null}
        {tab === "history" ? <HistoryTab activity={activity} work={work} onOpen={(id) =>
          void navigate({
            to: "/records/$recordId",
            params: { recordId: id },
            search: (prev) => ({ ...keepSearch(prev), tab: "history" }),
          })
        } /> : null}
      </RecordPage>
    </ScreenFrame>
  );
}

function isGrade(value: string): value is "A" | "B" | "C" | "D" {
  return value === "A" || value === "B" || value === "C" || value === "D";
}

// ----------------------------------------------------------------------------------- the rail

function RailDocument({ activity, onOpen }: { activity: Activity; onOpen: () => void }) {
  const documentId = activity.source_document_id ?? undefined;
  const read = useDocument(documentId);
  const { data: me } = useMe();
  // **The way to correct a record is a replacement bill, and only a role that may upload is offered
  // it** (ADR-0030: a correction is a new revision written by promotion from a replacement statement).
  // The header used to carry a "Correct this record" button that opened a drawer saying correcting in
  // place is not built, to every role including an Auditor; it could never do anything (U7).
  const replace = mayEnterData(me?.role) ? (
    <Link
      className={styles.ghost}
      to="/upload"
      search={keepSearch}
      data-testid="record-replace"
      title="A recorded figure is never edited: a replacement bill is verified and supersedes this one"
    >
      Upload a replacement bill
    </Link>
  ) : null;
  if (!documentId) return replace ?? <p className={styles.hint}>No document.</p>;
  if (read.error) {
    return (
      <div role="alert" data-testid="record-document-failed">
        <p className={styles.hint}>The document could not be read.</p>
        <button type="button" className={styles.ghost} onClick={() => void read.refetch()}>
          Try again
        </button>
        {replace}
      </div>
    );
  }
  return (
    <div className={styles.railDoc}>
      <b>{read.data ? (read.data.original_filename ?? "Untitled document") : "…"}</b>
      <button type="button" className={styles.ghost} onClick={onOpen}>
        Open
      </button>
      {replace}
    </div>
  );
}

function RailRevisions({ activity, open }: { activity: Activity; open: (id: string) => void }) {
  const reader = useReader();
  const latest = activity.revisions.slice(-3).reverse();
  return (
    <ul className={styles.railList}>
      {latest.map((revision, at) => (
        <li key={revision.activity_record_id}>
          {revision.activity_record_id === activity.activity_record_id ? (
            <span>
              Revision {activity.revisions.length - at}, here
            </span>
          ) : (
            <button type="button" className={styles.figureButton} onClick={() => open(revision.activity_record_id)}>
              Revision {activity.revisions.length - at}
            </button>
          )}{" "}
          <small>{instantDate(revision.created_at, reader)}</small>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------- Details

function DetailsTab({
  activity,
  origin,
  onReceipt,
  source,
}: {
  activity: Activity;
  origin: string;
  onReceipt: (calculationId: string) => void;
  source: SourceFields;
}) {
  return (
    <>
      <section className="card">
        <FieldGrid
          as="h2"
          testId="record-details"
          sections={[
            {
              title: "What was recorded",
              fields: [
                { label: "Site", value: activity.facility_name },
                { label: "Category", value: words(activity.category) },
                {
                  label: "Fuel or carrier",
                  value: <SourceValue source={source} of="carrier">{words(activity.fuel_or_carrier) || "—"}</SourceValue>,
                },
                {
                  label: "Quantity",
                  value: (
                    <SourceValue source={source} of="quantity">
                      {`${statedQuantity(activity.quantity)} ${unitLabel(activity.unit)}`}
                    </SourceValue>
                  ),
                },
                {
                  label: "Service period",
                  value: (
                    <SourceValue source={source} of="period">
                      {periodWords(activity.period_start, activity.period_end)}
                    </SourceValue>
                  ),
                },
                { label: "Method", value: words(activity.method) },
                { label: "Data quality", value: `Grade ${activity.quality}` },
                { label: "Source", value: isOrigin(origin) ? <SourceChip origin={origin} /> : "—" },
                // The verifier by name where the reader may be given one (the Admin's roster); the
                // ledger and its payloads still hold the identifier only (rule 4), and the identifier
                // is on hover. Every other role reads the sentence it always did.
                {
                  label: "Verifier",
                  value: activity.verification_id ? (
                    <PersonName
                      userId={activity.verification_id}
                      fallback="Accepted into the inventory by a named verifier"
                    />
                  ) : (
                    "None recorded"
                  ),
                },
                { label: "Record", value: activity.activity_record_id, mono: true, wide: true },
              ],
            },
            {
              title: "Figures priced from it",
              fields:
                activity.calculations.length === 0
                  ? [{ label: "Figures", value: "None yet. A gap, not a zero.", wide: true }]
                  : activity.calculations.map((calc) => ({
                      label: `Scope ${calc.scope} · ${words(calc.category)}${calc.method ? `, ${calc.method}-based` : ""}`,
                      value: (
                        <span className={styles.actions}>
                        <button
                          type="button"
                          className={styles.figureButton}
                          data-testid={`record-figure-${calc.calculation_id}`}
                          // Three spans a sighted reader takes in at once and a listener hears as
                          // "Scope 2 electricity market-based 102.40" with no idea which part is the figure.
                          aria-label={`Receipt for scope ${calc.scope} ${words(calc.category)}${
                            calc.method ? `, ${calc.method}-based` : ""
                          }: ${tonnes(calc.kgco2e, 2)} tonnes CO2 equivalent`}
                          onClick={() => onReceipt(calc.calculation_id)}
                        >
                          {tonnes(calc.kgco2e, 2)} tCO₂e
                        </button>
                        <BasisChip basis={activity.basis} />
                        </span>
                      ),
                    })),
            },
          ]}
        />
      </section>

      {/* Assurance questions about this record (gate 27). Nothing unless there are some. */}
      <AssuranceAbout
        recordId={activity.activity_record_id}
        frame={(children) => <Panel title={ASSURANCE_HEADING} column="1 / 13">{children}</Panel>}
      />
      {/* The way into the assurance queue from this record (gate 27). Roles that cannot raise one are
          shown nothing. */}
      <RaiseAboutThis
        recordId={activity.activity_record_id}
        frame={(children) => <Panel title="Assurance" column="1 / 13">{children}</Panel>}
      />
      {/* **Accepting a figure that has no document** (ADR-0067). Keyed by the record so a refusal or a
          confirmation belongs to one record and is not carried to the next. */}
      <VerifyStatedBasis key={activity.activity_record_id} activity={activity} />
    </>
  );
}

// ---------------------------------------------------------------------------------- Document

function DocumentTab({
  activity,
  source,
  scrollTo,
  onMark,
}: {
  activity: Activity;
  source: SourceFields;
  scrollTo: { field: string; n: number } | null;
  onMark: (field: string) => void;
}) {
  const documentId = activity.source_document_id ?? undefined;
  const documentRead = useDocument(documentId);
  const { data: document } = documentRead;
  // Presigned on opening this tab, not on loading the page: a URL that yields bytes is an access
  // event, and one recorded for a tab nobody opened is a false entry in an audit trail.
  const { data: url, isPending: urlPending, error: urlError } = useDocumentUrl(documentId, true);
  const receiptRead = useReceipt(activity.calculations[0]?.calculation_id);

  if (!documentId) {
    const receipt = receiptRead.data;
    return (
      <section className="card">
        <FieldGrid
          as="h2"
          sections={[
            {
              title: "No document",
              fields: [
                {
                  label: "Evidence",
                  wide: true,
                  value: receipt?.stated_basis
                    ? `Typed in by hand. Basis: ${receipt.stated_basis}`
                    : "Typed in by hand. There is no bill to show.",
                },
              ],
            },
          ]}
        />
        <p className={styles.tableNote}>
          <InfoPopover infoKey="record.no-document" />
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        {/* **A failed read is not a missing document** - the difference is the whole claim: "any
            figure from this record reports its chain as broken" is a statement about the evidence
            chain, and a dropped request produced it. */}
        {documentRead.error ? (
          <ReadFailed
            of="This record's source document"
            error={documentRead.error}
            onRetry={() => void documentRead.refetch()}
          />
        ) : documentRead.isPending ? (
          <Empty>Loading…</Empty>
        ) : !document ? (
          <EmptyState title="This record cites a document that could not be found." compact />
        ) : (
          <>
            <FieldGrid
              as="h2"
              sections={[
                {
                  title: "The document",
                  fields: [
                    { label: "File", value: document.original_filename ?? "Untitled document" },
                    { label: "Kind", value: words(document.kind) },
                    { label: "State", value: words(document.state) },
                    {
                      label: "Stated period",
                      value:
                        document.period_start && document.period_end
                          ? periodWords(document.period_start, document.period_end)
                          : "Not stated",
                    },
                    { label: "Digest", value: document.sha256.slice(0, 16), mono: true, wide: true },
                  ],
                },
              ]}
            />
            <div className={styles.actions} style={{ padding: "10px 14px" }}>
              {document.retention_expired ? (
                <StatusChip tone="warn" size="sm">
                  Past its retention date
                </StatusChip>
              ) : null}
              {urlError ? (
                <StatusChip tone="crit" size="sm">
                  No link could be issued
                </StatusChip>
              ) : (
                <a
                  className={styles.headLink}
                  href={url ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={urlPending || !url}
                >
                  {urlPending ? "Issuing a link…" : "Open the document"}
                </a>
              )}
              <Link
                className={styles.ghost}
                to="/evidence/$documentId"
                params={{ documentId: document.id }}
                search={keepSearch}
              >
                Open in the library
              </Link>
              <InfoPopover infoKey="record.document" />
            </div>
          </>
        )}
      </section>

      {/* The text EmitIQ read, with the characters each figure was read from marked where they can still
          be confirmed (DOC-03). One component, shared with the review screen: a second rendering of a
          span would be a second thing that could draw a highlight in the wrong place. */}
      {document ? (
        <>
          <ExtractedValues source={source} scrollTo={scrollTo} />
          <SourceTextPane
            documentId={documentId}
            enabled={!source.unread}
            selected={source.focus}
            onSelect={source.show}
            column="1 / 13"
            markAll
            details={source.details}
            onMark={onMark}
            accepted={document.state === "verified"}
          />
        </>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------------------- History

function HistoryTab({
  activity,
  work,
  onOpen,
}: {
  activity: Activity;
  work: Work | null | undefined;
  onOpen: (id: string) => void;
}) {
  const reader = useReader();
  const chain = activity.revisions;
  const columns: GridColumn<(typeof chain)[number]>[] = [
    {
      id: "revision",
      header: "Revision",
      rowHeader: true,
      cell: (revision) => `Revision ${String(chain.indexOf(revision) + 1)}`,
    },
    {
      id: "state",
      header: "State",
      cell: (revision) => (
        <span className={styles.actions}>
          {revision.is_effective ? (
            <StatusChip tone="ok" size="sm">
              In force
            </StatusChip>
          ) : (
            <StatusChip tone="warn" size="sm">
              Superseded
            </StatusChip>
          )}
          {revision.activity_record_id === activity.activity_record_id ? (
            <StatusChip tone="info" size="sm">
              You are here
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      id: "quantity",
      header: "Quantity",
      numeric: true,
      cell: (revision) => `${statedQuantity(revision.quantity)} ${unitLabel(revision.unit)}`,
    },
    { id: "period", header: "Period", cell: (revision) => periodWords(revision.period_start, revision.period_end) },
    { id: "method", header: "Method", cell: (revision) => words(revision.method) },
    { id: "grade", header: "Grade", cell: (revision) => revision.quality },
    { id: "recorded", header: "Recorded", cell: (revision) => instantDate(revision.created_at, reader) },
    {
      id: "open",
      header: "Open",
      cell: (revision) =>
        revision.activity_record_id === activity.activity_record_id ? (
          "—"
        ) : (
          <button type="button" className={styles.figureButton} onClick={() => onOpen(revision.activity_record_id)}>
            Open this revision
          </button>
        ),
    },
  ];
  return (
    <>
      <RecordTimeline activity={activity} work={work} />
      <section className="card">
        <h2 className={styles.cardHead}>
          Revision history
          <InfoPopover infoKey="record.revisions" />
        </h2>
        <GridTable
          caption="Every revision of this record, oldest first"
          columns={columns}
          rows={chain}
          rowKey={(revision) => revision.activity_record_id}
          rowNumbers={false}
          testId="record-revisions"
          empty={<EmptyState title="This record has never been corrected." compact />}
        />
      </section>
    </>
  );
}

export function RecordScreen() {
  return (
    <RequireSession>
      <RecordScreenBody />
    </RequireSession>
  );
}
